// src/components/Auth/SignupSuccessScreen.tsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';

interface SignupSuccessScreenProps {
  email?: string;
  name?: string;
  onContinue: () => void;
  autoRedirectDelay?: number; // Time in ms before auto-redirecting
  messageText?: string;
}

export function SignupSuccessScreen({
  email,
  name,
  onContinue,
  autoRedirectDelay = 10000, // Default 10 seconds
  messageText = "Your account has been created successfully."
}: SignupSuccessScreenProps) {
  // Auto-redirect after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      onContinue();
    }, autoRedirectDelay);

    return () => clearTimeout(timer);
  }, [onContinue, autoRedirectDelay]);

  // Get welcome message based on available info
  const getWelcomeMessage = () => {
    if (name) {
      return `Welcome to Tripper, ${name}!`;
    }
    return 'Welcome to Tripper!';
  };

  return (
    <>
      {/* Header */}
      <div className="px-6 flex flex-col justify-center border-b border-gray-200" style={{ height: '90px' }}>
        <h2 className="text-2xl font-regular text-gray-900">Account Created</h2>
        <p className="text-sm text-gray-500 mt-1">
          Your account has been successfully created
        </p>
      </div>
      {/* <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Account Created</h2>
        <p className="text-sm text-gray-500 mt-1">
          Your account has been successfully created
        </p>
      </div> */}
  
      {/* Content */}
      <div className="p-6 flex flex-col items-center">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, type: 'spring' }}
          className="mb-8"
        >
          <CheckCircle size={64} className="text-green-500" />
        </motion.div>
  
        <h2 className="text-xl font-semibold text-center mb-6">
          {getWelcomeMessage()}
        </h2>
        
        <p className="text-gray-600 text-center mb-8">
          {messageText}
        </p>
  
        {email && (
          <div className="flex flex-col bg-blue-50 p-4 rounded-lg mb-8 w-full">
            <p className="text-sm text-gray-700 mb-3">
              A confirmation email has been sent to:
            </p>
            <p className="font-medium text-blue-700 mb-3">{email}</p>
            <p className="text-xs text-gray-500 mt-2">
              Please check your inbox and confirm your email address.
            </p>
          </div>
        )}
  
        {/* Preloader section - moved above button */}
        <div className="w-full mb-8">
          <div className="flex items-center mb-2">
            <span className="font-medium text-green-700 text-xl mr-2">
              {Math.ceil(autoRedirectDelay / 1000)}
            </span>
            <span className="text-sm text-gray-700">
              seconds before you need to re-login
            </span>
          </div>
          
          {/* Inverted progress bar */}
          <div className="w-full h-2 bg-green-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-green-500"
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{ duration: autoRedirectDelay / 1000, ease: 'linear' }}
            />
          </div>
          
         {/*  <p className="text-green-600 text-sm mt-2">
            Please prepare to log in
          </p> */}
        </div>
  
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="w-full"
        >
          <button
            onClick={onContinue}
            className="w-full bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            style={{
              height: 'var(--auth-button-height)',
              padding: 'var(--auth-button-padding)',
              fontSize: 'var(--auth-button-font-size)',
              fontWeight: 'var(--auth-button-font-weight)',
              borderRadius: 'var(--auth-button-border-radius)'
            }}
          >
            Continue to Tripper
          </button>
        </motion.div>
      </div>
    </>
  );
}